/**
 * View Curation Layer
 *
 * Classifies semantic views into recommended/secondary/advanced
 * using heuristics based on viewType, metadata richness, and naming.
 */

import type { DataModelView } from '../../api/dataModelsService';

export type ViewImportance = 'primary' | 'secondary' | 'technical';
export type BusinessArea = 'sales' | 'finance' | 'marketing' | 'support' | 'operations' | 'general';
export type ViewComplexity = 'simple' | 'advanced';

export interface CuratedView {
  view: DataModelView;
  importance: ViewImportance;
  businessArea: BusinessArea;
  complexity: ViewComplexity;
  recommendedForDashboard: boolean;
  displayName: string;
  shortDescription: string;
  keyMetrics: string[];
  exampleQuestions: string[];
  tableCount: number;
  columnCount: number;
}

// ─── Business-friendly name conversion ────────────────────────────────────────

const TECHNICAL_SUFFIXES = /_fact_view$|_multi_fact$|_dimension$|_dim$|_bridge$|_helper$/i;

function toDisplayName(view: DataModelView): string {
  if (view.title) return view.title;
  // Strip technical suffixes and convert snake_case to Title Case
  const cleaned = view.name.replace(TECHNICAL_SUFFIXES, '');
  return cleaned
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Business area inference ──────────────────────────────────────────────────

const AREA_KEYWORDS: Record<BusinessArea, RegExp> = {
  sales: /sales|pipeline|opportunity|deal|revenue|lead|quota|forecast/i,
  finance: /finance|accounting|invoice|payment|expense|budget|cost/i,
  marketing: /marketing|campaign|channel|conversion|funnel|engagement/i,
  support: /support|ticket|case|resolution|sla|customer.service/i,
  operations: /operations|inventory|shipping|logistics|supply|warehouse/i,
  general: /./,
};

function inferBusinessArea(view: DataModelView): BusinessArea {
  const text = `${view.name} ${view.title || ''} ${view.description || ''} ${view.descriptionJson?.summary || ''}`;
  for (const [area, regex] of Object.entries(AREA_KEYWORDS) as [BusinessArea, RegExp][]) {
    if (area === 'general') continue;
    if (regex.test(text)) return area;
  }
  return 'general';
}

// ─── Classification ───────────────────────────────────────────────────────────

function classifyView(view: DataModelView): { importance: ViewImportance; complexity: ViewComplexity; recommendedForDashboard: boolean } {
  const vt = view.viewType?.toLowerCase() || '';
  const hasDescription = !!(view.descriptionJson?.summary || view.description);
  const hasQuestions = !!(view.suggestedQuestions && view.suggestedQuestions.length > 0);
  const hasMetrics = !!(view.columnsJson && Array.isArray(view.columnsJson) && view.columnsJson.length > 0);

  // Multi-fact / cross-domain → promote to primary if rich metadata
  if (vt.includes('multi_fact') || vt.includes('multi-fact')) {
    if (hasDescription && hasQuestions) {
      return { importance: 'primary', complexity: 'advanced', recommendedForDashboard: true };
    }
    if (hasDescription || hasMetrics) {
      return { importance: 'secondary', complexity: 'advanced', recommendedForDashboard: false };
    }
    return { importance: 'technical', complexity: 'advanced', recommendedForDashboard: false };
  }

  // Dimension-only views → secondary
  if (vt === 'dimension') {
    return { importance: 'secondary', complexity: 'simple', recommendedForDashboard: false };
  }

  // Fact views with good metadata → primary/recommended
  if (vt === 'fact' || vt.includes('fact')) {
    if (hasDescription && hasQuestions) {
      return { importance: 'primary', complexity: 'simple', recommendedForDashboard: true };
    }
    if (hasDescription || hasMetrics) {
      return { importance: 'primary', complexity: 'simple', recommendedForDashboard: true };
    }
    return { importance: 'secondary', complexity: 'simple', recommendedForDashboard: false };
  }

  // Fallback: if has rich metadata, secondary; else technical
  if (hasDescription && hasQuestions) {
    return { importance: 'secondary', complexity: 'simple', recommendedForDashboard: false };
  }
  return { importance: 'technical', complexity: 'advanced', recommendedForDashboard: false };
}

// ─── Metrics extraction ───────────────────────────────────────────────────────

function extractKeyMetrics(view: DataModelView): string[] {
  // Prefer pre-curated key_metrics from backend (business curation LLM output)
  if (view.keyMetrics && view.keyMetrics.length > 0) {
    return view.keyMetrics.slice(0, 3);
  }
  // Fallback: extract from column metadata
  if (!view.columnsJson || !Array.isArray(view.columnsJson)) return [];
  const metrics: string[] = [];
  for (const col of view.columnsJson as Array<Record<string, unknown>>) {
    const role = (col.semantic_type as string) || (col.role as string) || '';
    if (/measure|metric|kpi/i.test(role)) {
      const label = (col.label as string) || (col.name as string) || '';
      if (label) metrics.push(label);
    }
    if (metrics.length >= 3) break;
  }
  return metrics.slice(0, 3);
}

function extractExampleQuestions(view: DataModelView): string[] {
  if (view.suggestedQuestions && view.suggestedQuestions.length > 0) {
    return view.suggestedQuestions.slice(0, 4);
  }
  if (view.descriptionJson?.business_questions) {
    const bq = view.descriptionJson.business_questions;
    if (Array.isArray(bq)) return bq.slice(0, 4);
    if (typeof bq === 'string') return bq.split('\n').filter(Boolean).slice(0, 4);
  }
  return [];
}

// ─── Main curation function ───────────────────────────────────────────────────

export function curateViews(views: DataModelView[]): CuratedView[] {
  return views.map((view) => {
    const { importance, complexity, recommendedForDashboard } = classifyView(view);
    const columnCount = Array.isArray(view.columnsJson) ? view.columnsJson.length : 0;
    const tableCount = Array.isArray(view.baseTables) ? view.baseTables.length : 0;

    return {
      view,
      importance,
      businessArea: inferBusinessArea(view),
      complexity,
      recommendedForDashboard,
      displayName: toDisplayName(view),
      shortDescription: view.descriptionJson?.summary || view.description || '',
      keyMetrics: extractKeyMetrics(view),
      exampleQuestions: extractExampleQuestions(view),
      tableCount,
      columnCount,
    };
  });
}

/** Get recommended views (primary importance, sorted by sortOrder) */
export function getRecommendedViews(curated: CuratedView[]): CuratedView[] {
  return curated
    .filter((v) => v.importance === 'primary')
    .sort((a, b) => a.view.sortOrder - b.view.sortOrder)
    .slice(0, 8);
}

/** Get secondary views grouped by business area */
export function getSecondaryViews(curated: CuratedView[]): Map<BusinessArea, CuratedView[]> {
  const map = new Map<BusinessArea, CuratedView[]>();
  for (const v of curated.filter((c) => c.importance === 'secondary')) {
    const list = map.get(v.businessArea) || [];
    list.push(v);
    map.set(v.businessArea, list);
  }
  return map;
}

/** Get advanced/technical views */
export function getAdvancedViews(curated: CuratedView[]): CuratedView[] {
  return curated.filter((v) => v.importance === 'technical');
}